import { PICK_COUNT } from "../../../constants/game.ts";
import { useCombinedQueries } from "../../../hooks/use-combined-queries.ts";
import { useCurrentGameQuery } from "./use-current-game.ts";
import { useCurrentGamePicks } from "./use-current-game-picks.ts";

export type UseGameRedirectResult =
  | {
      isPending: true;
      isError: false;
      error: null;
      to: null;
    }
  | {
      isPending: false;
      isError: true;
      error: Error | null;
      to: null;
    }
  | {
      isPending: false;
      isError: false;
      error: null;
      to: "SPLASH_PAGE" | "PICK_PLAYERS_PAGE" | "PLAY_GAME_PAGE";
    };

/**
 * This hook is responsible for determining the page to which the user should go
 * based on the current game state and the user's picks.
 *
 * 1. If the game is upcoming, redirect to the splash page.
 * 2. If the user has not made all their picks and the game is open, redirect
 *    them to pick players.
 * 3. If the user has not made picks and the game is finished, redirect them
 *    to the splash page.
 * 4. If the user has made all their picks, redirect them to play the game.
 */
export function useContextualRedirect(): UseGameRedirectResult {
  const { isPending, isError, error, data } = useCombinedQueries(
    useCurrentGamePicks(),
    useCurrentGameQuery(),
  );

  if (isPending)
    return { isPending: true, isError: false, error: null, to: null };

  if (isError) return { isPending: false, isError: true, error, to: null };

  const [picks, game] = data;

  if (game.status === "upcoming") {
    return { isPending: false, isError: false, error: null, to: "SPLASH_PAGE" };
  }

  const hasPicks = picks.length === PICK_COUNT;

  if (!hasPicks) {
    if (game.status === "finished") {
      return {
        isPending: false,
        isError: false,
        error: null,
        to: "SPLASH_PAGE",
      };
    }

    return {
      isPending: false,
      isError: false,
      error: null,
      to: "PICK_PLAYERS_PAGE",
    };
  }

  return {
    isPending: false,
    isError: false,
    error: null,
    to: "PLAY_GAME_PAGE",
  };
}
